import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { AlertCircle, Check, Loader2, Mail } from "lucide-react";
import confetti from "canvas-confetti";
import { StepIndicator } from "@/components/create/StepIndicator";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

import { track } from "@/lib/analytics";

export const Route = createFileRoute("/_authenticated/create/processing/$id")({
  head: () => ({
    meta: [
      { title: "生成中 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProcessingPage,
});

// 品質ティアごとの平均所要時間（秒） - 実測ベースで余裕を持たせた値
const ETA_SECONDS_BY_TIER: Record<string, number> = {
  trial: 600,        // omni 720p 10s
  standard: 360,     // gen3a turbo
  high: 900,         // omni 1080p 10s
  premium: 900,      // omni 1080p 10s + audio
  lipsync: 360,      // talking photo
  i2v_standard: 480, // i2v 720p 5s
  i2v_high: 600,     // i2v 1080p 5s
  i2v_long: 1200,    // i2v 1080p 10s (実測 ~10-20分)
  story_std: 1200,
  story_pro: 1500,
};
const DEFAULT_ETA_SECONDS = 600;

type GenerationStatus = "pending" | "processing" | "completed" | "failed" | string;

type GenerationRow = {
  status: GenerationStatus;
  output_video_url: string | null;
  error_message: string | null;
  created_at: string | null;
  quality_tier: string | null;
};

const STAGES = [
  { key: "analyze", label: "素材を解析中" },
  { key: "ai", label: "AIが処理中" },
  { key: "render", label: "動画を生成中" },
  { key: "finalize", label: "最終調整中" },
] as const;

function formatRemaining(sec: number): string {
  if (sec <= 0) return "まもなく完了";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (m === 0) return `あと約 ${s} 秒`;
  return `あと約 ${m} 分`;
}

function ProcessingPage() {
  const { id } = useParams({ from: "/_authenticated/create/processing/$id" });
  const navigate = useNavigate();
  const [gen, setGen] = useState<GenerationRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const firedConfettiRef = useRef(false);

  // 1秒ごとに現在時刻を更新
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  // 初回フェッチ + Realtime購読 + フォールバックポーリング
  useEffect(() => {
    if (!id || id === "undefined") {
      setLoadError("生成を開始できませんでした。もう一度お試しください。");
      return;
    }

    let active = true;

    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from("generations")
        .select("status, output_video_url, error_message, created_at, quality_tier")
        .eq("id", id)
        .maybeSingle();
      if (!active) return;
      if (error) {
        setLoadError("生成状況の確認に失敗しました");
        return;
      }
      if (!data) {
        setLoadError("生成データが見つかりませんでした。");
        return;
      }
      setGen(data as GenerationRow);
    };

    void fetchOnce();

    // Realtime: generations の該当行を購読
    const channel = supabase
      .channel(`gen-${id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "generations", filter: `id=eq.${id}` },
        (payload) => {
          if (!active) return;
          setGen((prev) => ({ ...(prev ?? {} as GenerationRow), ...(payload.new as GenerationRow) }));
        },
      )
      .subscribe();

    // バックアップ: 10秒に1度DB再フェッチ（Realtime喪失保険）
    const dbTimer = window.setInterval(fetchOnce, 10000);

    // フォールバック: 30秒ごとにプロバイダAPIを直接ポーリング
    // (Edge Function を直接呼ぶことで、serverFn middleware で auth が切れた際に
    //  Response 例外がグローバル error reporter に拾われる問題を回避)
    const providerTimer = window.setInterval(() => {
      supabase.functions
        .invoke("check-generation", { body: { generation_id: id } })
        .catch((e) => {
          console.warn("fallback poll failed", e);
        });
    }, 30000);

    return () => {
      active = false;
      supabase.removeChannel(channel);
      window.clearInterval(dbTimer);
      window.clearInterval(providerTimer);
    };
  }, [id]);

  const status: GenerationStatus = gen?.status ?? "pending";
  const startedAt = gen?.created_at ? new Date(gen.created_at).getTime() : null;
  const etaSeconds = (gen?.quality_tier && ETA_SECONDS_BY_TIER[gen.quality_tier])
    || DEFAULT_ETA_SECONDS;
  const elapsedSec = startedAt ? Math.max(0, (now - startedAt) / 1000) : 0;
  const ratio = startedAt ? elapsedSec / etaSeconds : 0;
  const rawProgress = ratio <= 1 ? ratio * 95 : 95 + (1 - 1 / (1 + (ratio - 1) * 2)) * 4;
  const progress = Math.min(99, Math.max(2, rawProgress));
  const remainingSec = Math.max(0, etaSeconds - elapsedSec);

  // 現在のステージを決定
  const currentStageIndex = useMemo(() => {
    if (status === "pending") return 0; // 素材を解析中
    if (status === "processing") {
      if (elapsedSec < 30) return 1; // AIが処理中
      if (ratio < 0.8) return 2; // 動画を生成中
      return 3; // 最終調整中
    }
    return STAGES.length - 1;
  }, [status, elapsedSec, ratio]);

  // 完了時: 紙吹雪 → 結果ページへ遷移
  useEffect(() => {
    if ((status === "completed" || status === "succeeded") && !firedConfettiRef.current) {
      firedConfettiRef.current = true;
      const startedAt = gen?.created_at ? new Date(gen.created_at).getTime() : null;
      const durationSec = startedAt ? Math.round((Date.now() - startedAt) / 1000) : null;
      track("generation_complete", {
        quality_tier: gen?.quality_tier ?? null,
        duration_seconds: durationSec,
        success: true,
      });
      try {
        confetti({
          particleCount: 120,
          spread: 80,
          origin: { y: 0.6 },
        });
      } catch {
        // SSR/non-DOM環境では無視
      }
      const t = window.setTimeout(() => {
        navigate({ to: "/result/$id", params: { id }, replace: true });
      }, 1200);
      return () => window.clearTimeout(t);
    }
  }, [status, id, navigate, gen?.created_at, gen?.quality_tier]);

  // 失敗時: イベント発火（一度だけ）
  const firedFailedRef = useRef(false);
  useEffect(() => {
    if ((status === "failed" || status === "error") && !firedFailedRef.current) {
      firedFailedRef.current = true;
      track("generation_failed", {
        quality_tier: gen?.quality_tier ?? null,
        error: gen?.error_message ?? null,
      });
    }
  }, [status, gen?.quality_tier, gen?.error_message]);

  // ----- エラー / 失敗 -----
  if (loadError) {
    return <FailureView reason={loadError} />;
  }
  if (status === "failed" || status === "error") {
    return <FailureView reason={gen?.error_message ?? "動画生成に失敗しました。"} />;
  }

  // ----- 進行中 -----
  const isCompleting = status === "completed" || status === "succeeded";

  return (
    <main className="min-h-screen bg-background px-4 py-8 sm:py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-10">
          <StepIndicator current={4} />
        </div>

        <div className="flex flex-col items-center gap-8 py-8 text-center">
          {/* 円形プログレス */}
          <CircularProgress progress={progress} completed={isCompleting} />

          <div className="space-y-1">
            <h1 className="text-2xl font-bold">
              {isCompleting ? "完成しました！" : "生成中..."}
            </h1>
            <p className="text-sm text-muted-foreground tabular-nums">
              {isCompleting ? "結果ページへ移動します" : formatRemaining(remainingSec)}
            </p>
          </div>

          {/* ステップ進捗 */}
          <ol className="w-full max-w-md space-y-2">
            {STAGES.map((stage, idx) => {
              const done = idx < currentStageIndex || isCompleting;
              const active = idx === currentStageIndex && !isCompleting;
              return (
                <li
                  key={stage.key}
                  className={cn(
                    "flex items-center gap-3 rounded-md border p-3 text-left text-sm transition-colors",
                    done && "border-primary/40 bg-primary/5 text-foreground",
                    active && "border-primary bg-primary/10 text-foreground shadow-sm",
                    !done && !active && "border-border bg-muted/30 text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold",
                      done && "border-primary bg-primary text-primary-foreground",
                      active && "border-primary text-primary",
                      !done && !active && "border-muted-foreground/40",
                    )}
                  >
                    {done ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : active ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <span className="font-medium">{stage.label}</span>
                </li>
              );
            })}
          </ol>

          {/* メッセージ */}
          <Card className="w-full max-w-md p-4 text-left">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="space-y-1 text-sm">
                <p className="font-medium">画面を閉じても大丈夫です</p>
                <p className="text-muted-foreground">
                  完了したらメールでお知らせします。生成は継続します。
                </p>
              </div>
            </div>
          </Card>

          {/* ダッシュボードに戻る */}
          <Button asChild variant="outline">
            <Link to="/dashboard">ダッシュボードに戻る</Link>
          </Button>
        </div>
      </div>
    </main>
  );
}

function CircularProgress({ progress, completed }: { progress: number; completed: boolean }) {
  const size = 160;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, progress) / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          className="fill-none stroke-primary/15"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="fill-none stroke-primary transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {completed ? (
          <Check className="h-10 w-10 text-primary" />
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="mt-1 text-sm font-semibold tabular-nums">
              {Math.floor(progress)}%
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function FailureView({ reason }: { reason: string }) {
  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-xl">
        <Card className="p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-7 w-7 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">申し訳ありません、生成に失敗しました</h1>
          <p className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm font-medium text-foreground">
            消費したクレジットは返却されました
          </p>
          <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-left text-xs text-destructive">
            {reason}
          </div>
          <div className="mt-6 flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
            <Button asChild>
              <Link to="/create">もう一度試す</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/dashboard">ダッシュボードに戻る</Link>
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
